/* Karaoke Studio — shared lyrics timeline module.
 *
 * One source of truth for synced lyric display, the sync editor (Phase 3) and
 * the video burn-in (Phase 4). A "timeline" is an array of line objects:
 *
 *   { startS:Number, endS:Number, text:String, words?:[{startS,text}] }
 *
 * parseLRC understands both plain LRC ([mm:ss.cc] line tags) and Enhanced LRC
 * (per-word <mm:ss.cc> tags). serializeLRC turns a timeline back into a .lrc
 * string. render() builds the DOM once; update() is RAF-cheap and idempotent
 * for arbitrary seek times (binary search, cached active index).
 *
 * Scrolling is done ONLY through the container's scrollTop — never a
 * page-level scrollIntoView/scrollTo (global no-auto-scroll rule).
 */
(function () {
    'use strict';

    var LINE_TAG = /\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]/g;
    var WORD_TAG = /<(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?>/g;

    function toSeconds(min, sec, frac) {
        var s = parseInt(min, 10) * 60 + parseInt(sec, 10);
        if (frac != null && frac !== '') {
            // Normalise centi/milli-seconds to a fraction.
            var f = String(frac);
            while (f.length < 3) f += '0';
            s += parseInt(f.slice(0, 3), 10) / 1000;
        }
        return s;
    }

    function fmtTimeTag(s, open, close) {
        if (s < 0 || isNaN(s)) s = 0;
        var m = Math.floor(s / 60);
        var rest = s - m * 60;
        var whole = Math.floor(rest);
        var cc = Math.round((rest - whole) * 100);
        if (cc === 100) { cc = 0; whole += 1; }
        var pad = function (n, w) { n = String(n); while (n.length < w) n = '0' + n; return n; };
        return open + pad(m, 2) + ':' + pad(whole, 2) + '.' + pad(cc, 2) + close;
    }

    // ── Parse LRC / Enhanced LRC into a timeline ────────────────────────────
    // The [offset:±ms] metadata tag is part of the format and real files carry
    // it (it is how a publisher compensates for a lag between their timing and
    // the released master). Per the spec a POSITIVE offset makes the lyrics
    // appear sooner, so the effective time is stamp − offset/1000. Ignoring the
    // tag silently desynced every file that used one.
    var OFFSET_TAG = /\[offset:\s*([+-]?\d+)\s*\]/i;

    function parseLRC(text) {
        var src = String(text || '');
        var offsetM = src.match(OFFSET_TAG);
        var offsetS = offsetM ? parseInt(offsetM[1], 10) / 1000 : 0;
        if (!isFinite(offsetS)) offsetS = 0;
        var rawLines = src.split(/\r?\n/);
        var entries = [];
        var hasWords = false;

        for (var i = 0; i < rawLines.length; i++) {
            var line = rawLines[i];
            LINE_TAG.lastIndex = 0;
            var stamps = [];
            var m;
            while ((m = LINE_TAG.exec(line)) !== null) {
                stamps.push(toSeconds(m[1], m[2], m[3]));
            }
            if (!stamps.length) continue;

            // Body = everything after the last line tag.
            var body = line.slice(LINE_TAG.lastIndex || line.lastIndexOf(']') + 1);
            // Fallback: strip all line tags if lastIndex bookkeeping drifted.
            body = body.replace(LINE_TAG, '');

            // Word-level tags inside the body → words array.
            var words = null;
            if (WORD_TAG.test(body)) {
                hasWords = true;
                words = [];
                var cursor = 0;
                WORD_TAG.lastIndex = 0;
                var w, lastWordStart = null, lastTextStart = 0;
                var firstWordTagAt = body.search(WORD_TAG);
                // Text before the first <> tag belongs to the line start.
                var lead = firstWordTagAt > 0 ? body.slice(0, firstWordTagAt) : '';
                WORD_TAG.lastIndex = 0;
                while ((w = WORD_TAG.exec(body)) !== null) {
                    if (lastWordStart !== null) {
                        words.push({ startS: lastWordStart, text: body.slice(lastTextStart, w.index) });
                    }
                    lastWordStart = toSeconds(w[1], w[2], w[3]);
                    lastTextStart = WORD_TAG.lastIndex;
                }
                if (lastWordStart !== null) {
                    words.push({ startS: lastWordStart, text: body.slice(lastTextStart) });
                }
                if (lead.trim() && words.length) {
                    words.unshift({ startS: stamps[0], text: lead });
                }
                body = body.replace(WORD_TAG, '');
            }

            var txt = body.trim();
            if (!txt) continue;
            for (var s = 0; s < stamps.length; s++) {
                entries.push({ startS: stamps[s], endS: 0, text: txt, words: words });
            }
        }

        // Apply the file's global offset before the timeline is built, so endS
        // and any word stamps stay consistent with the shifted line starts.
        if (offsetS) {
            for (var o = 0; o < entries.length; o++) {
                entries[o].startS = Math.max(0, entries[o].startS - offsetS);
                if (entries[o].words) {
                    for (var w2 = 0; w2 < entries[o].words.length; w2++) {
                        entries[o].words[w2].startS = Math.max(0, entries[o].words[w2].startS - offsetS);
                    }
                }
            }
        }

        entries.sort(function (a, b) { return a.startS - b.startS; });
        for (var k = 0; k < entries.length; k++) {
            entries[k].endS = (k + 1 < entries.length) ? entries[k + 1].startS : entries[k].startS + 4;
        }
        return { lines: entries, hasWords: hasWords, offsetS: offsetS };
    }

    // ── Even-distribution timeline from plain text ──────────────────────────
    function fromPlainText(text, durationS) {
        var lines = String(text || '').split(/\r?\n/)
            .map(function (l) { return l.trim(); })
            .filter(function (l) { return l; });
        var dur = durationS > 0 ? durationS : lines.length;
        var interval = dur / (lines.length + 1);
        var out = [];
        for (var i = 0; i < lines.length; i++) {
            var start = interval * (i + 0.5);
            out.push({
                startS: start,
                endS: interval * (i + 1.5),
                text: lines[i],
                words: null,
            });
        }
        return out;
    }

    // ── SubtitleParser cues → timeline ──────────────────────────────────────
    // SubtitleParser (SRT/VTT) speaks {start,end,text} in seconds; the timeline
    // speaks {startS,endS,text}. One adapter here rather than every caller
    // renaming fields. A cue's own end is KEPT (unlike LRC, a subtitle format
    // states when a line leaves the screen), so a gap between two cues stays a
    // gap instead of becoming a line that hangs on until the next one.
    function fromCues(cues) {
        var out = (cues || [])
            .filter(function (c) { return c && String(c.text || '').trim(); })
            .map(function (c) {
                return {
                    startS: +c.start || 0,
                    endS: (+c.end > +c.start) ? +c.end : (+c.start || 0) + 4,
                    // Multi-line cues are one lyric line: the wrap is the
                    // painter's job and depends on the frame width.
                    text: String(c.text).replace(/\s*\n\s*/g, ' ').trim(),
                    words: null,
                };
            });
        out.sort(function (a, b) { return a.startS - b.startS; });
        return out;
    }

    // ── Accept whatever the user actually has ───────────────────────────────
    // Users arrive with .lrc, .srt, .vtt or a plain block of lyrics copied off a
    // web page, and they do not sort that out before uploading. Sniff the
    // content — never the file extension, which is routinely wrong.
    //
    // Returns { lines, hasWords, offsetS, format, timed }. `timed:false` means
    // the text carried no timing at all and the caller must time it (auto-draft
    // or the tap-sync editor) before it can be shown.
    function parseAny(text, opts) {
        opts = opts || {};
        var src = String(text == null ? '' : text);
        var trimmed = src.replace(/^\uFEFF/, '').trim();
        if (!trimmed) return { lines: [], hasWords: false, offsetS: 0, format: 'empty', timed: false };

        // LRC/eLRC: a [mm:ss] line tag. Checked BEFORE the arrow test because an
        // LRC file cannot contain '-->' but a VTT header can precede anything.
        if (LINE_TAG.test(trimmed)) {
            LINE_TAG.lastIndex = 0;
            var lrc = parseLRC(trimmed);
            if (lrc.lines.length) {
                lrc.format = lrc.hasWords ? 'elrc' : 'lrc';
                lrc.timed = true;
                return lrc;
            }
        }
        LINE_TAG.lastIndex = 0;

        // SRT / VTT via the shared parser.
        if (trimmed.indexOf('-->') >= 0) {
            var SP = (typeof window !== 'undefined') ? window.SubtitleParser : null;
            if (SP && typeof SP.parse === 'function') {
                var cues = SP.parse(trimmed);
                if (cues && cues.length) {
                    return {
                        lines: fromCues(cues),
                        hasWords: false,
                        offsetS: 0,
                        format: /^WEBVTT/i.test(trimmed) ? 'vtt' : 'srt',
                        timed: true,
                    };
                }
            }
        }

        // Plain lyrics. Deliberately NOT auto-spread here: handing back invented
        // timings that look real is how a "synced" export ends up unsynced. The
        // caller decides — auto-draft against the audio, or tap-sync.
        // A line that is only a bracketed mark — «[Verse]», «[Припев]», «[Chorus 2]»
        // from Genius, Suno and our own generator — is not sung: kept, it was
        // shown on the frame and handed to the aligner as words (22.10.2026).
        var plain = trimmed.split(/\r?\n/)
            .map(function (l) { return l.trim(); })
            .filter(function (l) { return l && !/^\[[^\[\]]{1,40}\]$/.test(l); });
        return {
            lines: plain.map(function (t) { return { startS: null, endS: null, text: t, words: null }; }),
            hasWords: false,
            offsetS: 0,
            format: 'plain',
            timed: false,
        };
    }

    // ── Shift / nudge ───────────────────────────────────────────────────────
    // A whole-timeline shift is the single most-used correction (the lyrics are
    // right but consistently early or late). Word stamps move with their line,
    // otherwise the wipe desyncs from the text it belongs to.
    function shift(timeline, deltaS) {
        return (timeline || []).map(function (ln) {
            var o = {
                startS: Math.max(0, ln.startS + deltaS),
                endS: (ln.endS != null) ? Math.max(0, ln.endS + deltaS) : ln.endS,
                text: ln.text,
                words: null,
            };
            if (ln.words && ln.words.length) {
                o.words = ln.words.map(function (w) {
                    return { startS: Math.max(0, w.startS + deltaS), text: w.text };
                });
            }
            return o;
        });
    }

    // Move ONE line, then repair the ordering so a nudge past a neighbour cannot
    // leave the timeline unsorted (activeIndexAt binary-searches — an unsorted
    // timeline silently shows the wrong line).
    function nudgeLine(timeline, index, deltaS) {
        var tl = (timeline || []).map(function (ln) { return ln; });
        if (index < 0 || index >= tl.length) return tl;
        var src = tl[index];
        var moved = {
            startS: Math.max(0, src.startS + deltaS),
            endS: src.endS,
            text: src.text,
            words: src.words ? src.words.map(function (w) {
                return { startS: Math.max(0, w.startS + deltaS), text: w.text };
            }) : null,
        };
        tl[index] = moved;
        tl.sort(function (a, b) { return a.startS - b.startS; });
        for (var k = 0; k < tl.length; k++) {
            tl[k] = {
                startS: tl[k].startS,
                endS: (k + 1 < tl.length) ? tl[k + 1].startS : Math.max(tl[k].startS + 4, tl[k].endS || 0),
                text: tl[k].text,
                words: tl[k].words,
            };
        }
        return tl;
    }

    // ── Serialize a timeline back to a .lrc string ──────────────────────────
    function serializeLRC(timeline) {
        var lines = (timeline || []).slice().sort(function (a, b) { return a.startS - b.startS; });
        var out = [];
        for (var i = 0; i < lines.length; i++) {
            var ln = lines[i];
            var row = fmtTimeTag(ln.startS, '[', ']');
            if (ln.words && ln.words.length) {
                for (var w = 0; w < ln.words.length; w++) {
                    row += fmtTimeTag(ln.words[w].startS, '<', '>') + ln.words[w].text;
                }
            } else {
                row += ln.text;
            }
            out.push(row);
        }
        return out.join('\n');
    }

    // ── Render the timeline into a container (build DOM once) ───────────────
    function render(container, timeline, opts) {
        opts = opts || {};
        container.innerHTML = '';
        container.__krActiveIdx = -2;
        for (var i = 0; i < timeline.length; i++) {
            var ln = timeline[i];
            var div = document.createElement('div');
            div.className = 'kr-lrc-line upcoming';
            div.dataset.index = i;
            if (ln.words && ln.words.length) {
                for (var w = 0; w < ln.words.length; w++) {
                    var span = document.createElement('span');
                    span.className = 'kr-lrc-word';
                    span.textContent = ln.words[w].text;
                    span.dataset.start = ln.words[w].startS;
                    div.appendChild(span);
                }
            } else {
                div.textContent = ln.text;
            }
            if (typeof opts.onSeek === 'function') {
                (function (startS) {
                    div.addEventListener('click', function () { opts.onSeek(startS); });
                })(ln.startS);
            }
            container.appendChild(div);
        }
    }

    // ── Binary search: index of the last line whose startS <= timeS ─────────
    function activeIndexAt(timeline, timeS) {
        var lo = 0, hi = timeline.length - 1, idx = -1;
        while (lo <= hi) {
            var mid = (lo + hi) >> 1;
            if (timeline[mid].startS <= timeS) { idx = mid; lo = mid + 1; }
            else { hi = mid - 1; }
        }
        return idx;
    }

    // ── Per-frame update: classes, word-wipe, centered scroll ───────────────
    // Idempotent for arbitrary seek times. Returns the active line index.
    function update(container, timeline, timeS, opts) {
        opts = opts || {};
        if (!timeline || !timeline.length) return -1;
        var idx = activeIndexAt(timeline, timeS);
        var nodes = container.children;

        if (container.__krActiveIdx !== idx) {
            container.__krActiveIdx = idx;
            for (var i = 0; i < nodes.length; i++) {
                var cls = nodes[i].classList;
                cls.remove('past', 'active', 'upcoming');
                cls.add(i < idx ? 'past' : (i === idx ? 'active' : 'upcoming'));
            }
            if (idx >= 0 && nodes[idx] && opts.scroll !== false) {
                var el = nodes[idx];
                container.scrollTop = el.offsetTop - container.clientHeight / 2 + el.offsetHeight / 2;
            }
        }

        // Word-wipe on the active line only (Enhanced LRC).
        if (idx >= 0 && nodes[idx]) {
            var line = timeline[idx];
            if (line.words && line.words.length) {
                var wordEls = nodes[idx].getElementsByClassName('kr-lrc-word');
                for (var w = 0; w < wordEls.length; w++) {
                    var ws = parseFloat(wordEls[w].dataset.start);
                    var sung = ws <= timeS;
                    if (sung !== wordEls[w].classList.contains('sung')) {
                        wordEls[w].classList.toggle('sung', sung);
                    }
                }
            }
        }
        return idx;
    }

    // ── Online synced-lyrics lookup (lrclib.net — free, CORS-open) ──────────
    // A single network call to a third-party community database. Optional —
    // manual paste / upload / the sync editor stay the primary path. Resolves
    // to results that actually carry synced (LRC) lyrics; rejects on network
    // failure / timeout so the caller can fall back to manual entry.
    function searchLRC(query) {
        query = query || {};
        var params = new URLSearchParams();
        if (query.artist) params.set('artist_name', query.artist);
        if (query.track) params.set('track_name', query.track);
        if (!query.artist && !query.track && query.q) params.set('q', query.q);
        if (!params.toString()) {
            return Promise.reject(new Error('empty-query'));
        }
        function withTimeout(url, ms) {
            var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
            var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, ms) : null;
            return fetch(url, {
                signal: ctrl ? ctrl.signal : undefined,
                headers: { 'Accept': 'application/json' },
            }).then(function (r) {
                if (timer) clearTimeout(timer);
                if (!r.ok) throw new Error('lrclib-' + r.status);
                return r.json();
            }, function (e) {
                if (timer) clearTimeout(timer);
                throw e;
            });
        }

        function normalize(arr) {
            return (arr || [])
                .filter(function (x) { return x && x.syncedLyrics; })
                .slice(0, 12)
                .map(function (x) {
                    return {
                        id: x.id,
                        // Our proxy already returns these names; lrclib uses its own.
                        artist: x.artist || x.artistName || '',
                        track: x.track || x.trackName || '',
                        duration: x.duration || 0,
                        syncedLyrics: x.syncedLyrics,
                    };
                });
        }

        // Own origin first: for RU visitors a direct call to a foreign host is
        // the request most likely to be blocked or throttled, and the origin
        // adds a shared 24 h cache. Direct lrclib stays as the fallback so the
        // feature still works if the proxy route is unavailable.
        return withTimeout('/api/lyrics-search?' + params.toString(), 9000)
            .then(normalize)
            .catch(function () {
                return withTimeout('https://lrclib.net/api/search?' + params.toString(), 8000)
                    .then(normalize);
            });
    }

    // ── Decode a lyrics file by what its bytes actually are ─────────────────
    // FileReader.readAsText assumes UTF-8. A large share of Russian .lrc files
    // in circulation are windows-1251 and came through as "\uFFFD\uFFFD\uFFFD\uFFFD\uFFFD\uFFFD" (measured on
    // a real CP1251 file, 2026-07-29). BOM first, then strict UTF-8, then 1251.
    function decodeBytes(buf) {
        var b = new Uint8Array(buf);
        if (b.length > 1 && b[0] === 0xFF && b[1] === 0xFE) return new TextDecoder('utf-16le').decode(buf);
        if (b.length > 1 && b[0] === 0xFE && b[1] === 0xFF) return new TextDecoder('utf-16be').decode(buf);
        if (b.length > 2 && b[0] === 0xEF && b[1] === 0xBB && b[2] === 0xBF) return new TextDecoder('utf-8').decode(buf.slice(3));
        try {
            return new TextDecoder('utf-8', { fatal: true }).decode(buf);
        } catch (_) {
            try { return new TextDecoder('windows-1251').decode(buf); }
            catch (_e) { return new TextDecoder('utf-8').decode(buf); }
        }
    }

    window.KaraokeLyrics = {
        parseLRC: parseLRC,
        parseAny: parseAny,
        fromCues: fromCues,
        shift: shift,
        nudgeLine: nudgeLine,
        decodeBytes: decodeBytes,
        fromPlainText: fromPlainText,
        serializeLRC: serializeLRC,
        render: render,
        update: update,
        activeIndexAt: activeIndexAt,
        searchLRC: searchLRC,
    };
})();
